import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AppStoreUrls } from '@anihis/shared/src/constants/app-store-ids';
import { environment } from '../../../environments/environment';
import { DesktopAppDownloadLinksComponent } from '../../shared/desktop-app-download-links/desktop-app-download-links.component';
import {
  detectMobilePlatform,
  storeUrlForPlatform,
  tryOpenMobileAppWithStoreFallback,
  type MobilePlatform,
} from '../../shared/mobile-app-open.util';

interface AppConfig {
  name: string;
  customScheme: string;
  androidPackage: string;
  appStoreUrl: string;
  playStoreUrl: string;
}

const APP_CONFIGS: Record<string, AppConfig> = {
  client: {
    name: 'Anihis',
    customScheme: 'com.anihis.app',
    androidPackage: 'com.anihis.app',
    appStoreUrl: AppStoreUrls.clientApp.ios,
    playStoreUrl: AppStoreUrls.clientApp.android,
  },
  veterinarian: {
    name: 'Anihis Vet',
    customScheme: 'com.anihis.veterinarian.app',
    androidPackage: 'com.anihis.veterinarian.app',
    appStoreUrl: AppStoreUrls.vetApp.ios,
    playStoreUrl: AppStoreUrls.vetApp.android,
  },
};

type RedirectStatus = 'loading' | 'redirecting-store' | 'desktop';

@Component({
  selector: 'anihis-app-download',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    MatProgressSpinnerModule,
    DesktopAppDownloadLinksComponent,
  ],
  templateUrl: './app-download.component.html',
  styleUrl: './app-download.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppDownloadComponent implements OnInit {
  private route = inject(ActivatedRoute);

  status = signal<RedirectStatus>('loading');
  appConfig: AppConfig | null = null;
  private mobilePlatform: MobilePlatform = 'desktop';

  ngOnInit(): void {
    const appType = this.route.snapshot.paramMap.get('appType') ?? 'client';
    this.appConfig = APP_CONFIGS[appType] ?? APP_CONFIGS['client'];
    this.handleRedirect();
  }

  openStore(): void {
    if (!this.appConfig) return;
    window.location.href = this.getStoreUrl();
  }

  getStoreUrl(): string {
    if (!this.appConfig) return '';
    return storeUrlForPlatform(this.mobilePlatform, this.appConfig);
  }

  getDesktopQrUrl(): string {
    const base = environment.portalUrl.replace(/\/$/, '');
    const appType =
      this.route.snapshot.paramMap.get('appType')?.trim() || 'client';
    return `${base}/download/${encodeURIComponent(appType)}`;
  }

  private handleRedirect(): void {
    if (!this.appConfig) return;

    const cfg = this.appConfig;
    const platform = tryOpenMobileAppWithStoreFallback({
      iosDeepLink: `${cfg.customScheme}://dashboard`,
      androidIntentPath: 'dashboard',
      androidPackage: cfg.androidPackage,
      customScheme: cfg.customScheme,
      appStoreUrl: cfg.appStoreUrl,
      playStoreUrl: cfg.playStoreUrl,
      onStoreRedirect: () => this.status.set('redirecting-store'),
    });
    this.mobilePlatform = platform;

    if (platform === 'desktop') {
      this.status.set('desktop');
    }
  }
}
